import { Empty, Flex, Skeleton } from "antd"
import { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"

import type { RootState } from "../../../../store.ts"
import { selectActiveBranchId } from "../../../Branches/redux/branches.slice.ts"
import { contractsSetFilters } from "../../redux/contracts.slice.ts"
import {
  fetchContracts,
  getContractFilterOptions
} from "../../redux/contracts.thunks.ts"
import { type ContractFilters } from "../../redux/contracts.types.ts"
import { ContractsSidebarHeader } from "./ContractsSidebarHeader.tsx"
import { ContractsSidebarList } from "./ContractsSidebarList.tsx"
import { buildStatusTabs } from "./status-tabs.ts"

function ContractsSidebar({
  result,
  filters,
  filterOptions,
  fetching,
  viewLeadAllowed,
  activeBranchId,
  getContracts,
  setFilters,
  getFilterOptions
}: ConnectedProps<typeof connector>) {
  const statuses = useMemo(
    () => buildStatusTabs(filterOptions?.status),
    [filterOptions?.status]
  )

  const handleLoadMoreContract = () => {
    if (fetching) return
  }

  const handleFilterChange = (
    key: keyof ContractFilters,
    value: string | null
  ) => {
    const next = { ...filters, [key]: value }
    setFilters({ [key]: value })
    getContracts(next)
  }

  useEffect(() => {
    getContracts(filters)
    getFilterOptions()
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the lists are reloaded only when the active branch changes, with the filters as they are at that moment
  }, [activeBranchId])

  return (
    <Flex
      vertical
      className={"max-w-[410px] border-r w-full shrink-0 border-border"}
    >
      <ContractsSidebarHeader
        onFilterChange={handleFilterChange}
        statuses={statuses}
        filters={filters}
      />

      {fetching ? (
        <Skeleton.Node className={"size-full"} active />
      ) : (
        <>
          {result?.results?.length ? (
            <ContractsSidebarList
              hasMore={result?.count > result?.results?.length}
              data={result?.results || []}
              viewLeadAllowed={viewLeadAllowed}
              onLoadMoreContracts={handleLoadMoreContract}
            />
          ) : (
            <Empty
              className={"my-4"}
              description={
                filters?.q ? "No contracts found" : "No contracts yet"
              }
            />
          )}
        </>
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.contracts.fetchingList,
  filters: state.contracts.filters,
  filterOptions: state.contracts.filterOptions,
  result: state.contracts.result,
  viewLeadAllowed:
    state?.auth?.userInfo?.permissions?.codes?.leads?.leads?.lead_view,
  activeBranchId: selectActiveBranchId(state)
})

const mapDispatchToProps = {
  getContracts: fetchContracts,
  setFilters: contractsSetFilters,
  getFilterOptions: getContractFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContractsSidebar)
